import { FC, useEffect } from 'react'
import { Col, Container, Row } from 'react-bootstrap'
import { useTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'
import { useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'

import { NAV_TO_LOGIN } from 'src/navigation'
import { shoot } from 'src/redux/actions'
import { select_public_authority_policy } from 'src/redux/store'
import { format_date, format_number } from 'src/utils'
import { Button } from 'src/widgets'

const PublicAuthorityPolicyDetail: FC = () => {
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const { t } = useTranslation()

  /**
   * Use Selector
   */
  const policy_detail = useSelector(select_public_authority_policy)

  /**
   * Use Effect
   */
  useEffect(() => {
    if (!policy_detail) navigate(NAV_TO_LOGIN)
  }, [policy_detail])

  /**
   * Render functions
   */
  const render_title = (title: string, className: string, padding: string) => {
    return (
      <Col className="p-0 mt-0 mb-lg-0 border-bottom">
        <main>
          <div className={`w-100 mt-5 ${'bg-ffffff'}`}>
            <Row className="m-0 p-0 d-lg-flex">
              <Row className={`m-0 ${padding} d-block`}>
                <div>
                  <p className={`${className}`}>{`${t(`${title}`)}`}</p>
                </div>
              </Row>
            </Row>
          </div>
        </main>
      </Col>
    )
  }

  const render_main_info_dx = (label: string, value: string) => {
    return (
      <Col lg={7} xl={3} className="m-0 p-0 d-flex flex-column ">
        <Row className="m-0 p-4 d-block">
          <div>
            <p className="m-0 p-0 font-16 c-7f7f7f">{`${label} \n`}</p>
            <span className="font-24">{`${value}`}</span>
          </div>
        </Row>
      </Col>
    )
  }

  const render_main_info = () => {
    return (
      <Col className="p-0 mb-4 mb-lg-0">
        <main>
          <div className={`w-100 ${'bg-ffffff'}`}>
            <Row className="m-0 p-0  d-lg-flex">
              <Col lg={7} xl={5} className="m-0 p-0 d-flex flex-column border-right">
                <Row className="m-0 p-4 d-block">
                  <div>
                    <p className="m-0 p-0 font-16 c-7f7f7f">{`${t(
                      'public_authority_policy.detail.status'
                    )} \n`}</p>
                    <span className="c-059881 font-24">
                      {policy_detail?.status?.split('- ')[1]}
                    </span>
                  </div>
                </Row>
              </Col>
              {render_main_info_dx(
                t('public_authority_policy.detail.effective_date'),
                policy_detail?.effectiveDate ? format_date(policy_detail?.effectiveDate) : ''
              )}
              {render_main_info_dx(
                t('public_authority_policy.detail.expiration_date'),
                policy_detail?.expirationDate ? format_date(policy_detail?.expirationDate) : ''
              )}
            </Row>
            <Row className="m-0 p-0  d-lg-flex">
              <Col lg={7} xl={5} className="m-0 p-0 d-flex flex-column border-right">
                <Row className="m-0 p-4 d-block">
                  <div>
                    <p className="m-0 p-0 font-16 c-7f7f7f">{`${t(
                      'public_authority_policy.detail.type'
                    )} \n`}</p>
                    <span className="font-24">{`${t(
                      'public_authority_policy.detail.policy_type'
                    )}`}</span>
                  </div>
                </Row>
              </Col>
              {render_main_info_dx(
                t('public_authority_policy.detail.emission_date'),
                policy_detail?.emissionDate ? format_date(policy_detail?.emissionDate) : ''
              )}
              {render_main_info_dx(
                t('public_authority_policy.detail.insured_amount'),
                format_number(policy_detail?.insuredAmount) + ' €'
              )}
            </Row>
          </div>
        </main>
      </Col>
    )
  }

  const render_property = () => {
    return (
      <>
        <div className="m-0 pl-4 pr-4 pt-3 pb-3 pb-0 pt-0 border-bottom">
          <div>
            <p className="m-0 p-0 mb-19 pb-2 font-16-semibold">{`${t(
              'public_authority_policy.detail.contractor'
            )}`}</p>
            <div className="public-authority-details">
              <p className="m-0 p-0 font-16 c-7f7f7f">{policy_detail?.contractor}</p>
              <p className="m-0 p-0 font-16 c-7f7f7f">{policy_detail?.taxCodeOrVatNumber}</p>
            </div>
          </div>
        </div>
        <Row className="m-0 pl-4 pr-4 pt-3 pb-3 pb-0 pt-0">
          <div>
            <p className="m-0 p-0 mb-19 pb-2 font-16-semibold">{`${t(
              'public_authority_policy.detail.beneficiary'
            )}`}</p>
            <p className="m-0 p-0 font-16 c-7f7f7f">{policy_detail?.contractingAuthority}</p>
          </div>
        </Row>
      </>
    )
  }

  const render_other_info = () => {
    return (
      <>
        {render_title(
          t('public_authority_policy.detail.details'),
          'm-0 p-0 font-14-semibold c-862633',
          'pl-4 pr-4 pt-3 pb-3'
        )}
        <div className={`w-100 ${'bg-ffffff'} border-bottom`}>
          <Row className="m-0 p-0  d-lg-flex w-100}">
            <Col lg={7} xl={6} className="m-0 p-0 d-flex flex-column border-right">
              {render_property()}
            </Col>

            <Col lg={7} xl={6} className="m-0 p-0 d-flex flex-column">
              <Row className="m-0 pl-4 pr-4 pt-3 pb-3 d-block">
                <div>
                  <p className="m-0 p-0 pb-2 mb-19 font-16-semibold">{`${t(
                    'public_authority_policy.detail.work_object'
                  )}`}</p>
                  <p className="m-0 p-0 font-16 c-7f7f7f">{policy_detail?.workObject}</p>
                </div>
              </Row>
            </Col>
          </Row>
        </div>
      </>
    )
  }

  const render_topbar = () => (
    <Col className="mt-2 p-0 d-flex justify-content-between align-items-center">
      <Button
        flavor="transparent_accent_back"
        text={t('public_authority_policy.detail.go_back')}
        classes="mt-2 px-0"
        on_press={() => {
          dispatch(shoot.reduce_public_authority_policy(null))
          navigate(-1)
        }}
      />
    </Col>
  )

  return (
    <Container fluid className="mb-5 pb-5 d-flex justify-content-center bg-f7f7f7">
      <Col lg={11}>
        {render_topbar()}
        <div>
          {/* TITLE */}
          {render_title(
            `${t('public_authority_policy.detail.policy_id')} ${policy_detail?.id?.policyNumber}`,
            'm-0 mt-3 mb-3 p-0 font-36-freightdisp-semibold',
            'p-4'
          )}
          {/* MAIN INFO */}
          {render_main_info()}
          {/* SEPARATOR */}
          <Col className="mt-5 border-top" />
          {/* OTHER INFO */}
          {render_other_info()}
        </div>
      </Col>
    </Container>
  )
}

export default PublicAuthorityPolicyDetail
